/**
 * Patient detail view + the print-as-PDF flow.
 *
 * `renderPatientDetail` renders the patient header, the OMON-035 draft
 * banner (when a draft exists), the Outcome Monitoring Plan summary
 * card with the consolidated trajectory chart, and the goals list.
 * `downloadPlanCopy` triggers the browser's native print flow scoped
 * to the .print-plan-region.
 *
 * `editPlan` is imported from `../main` (still there pending Phase 6b.5
 * extraction). ES module circular import — used inside an event handler
 * body, so the live binding resolves at click time.
 *
 * Extracted from main.ts as part of OMON-060 Phase 6b.4.
 */

import { ALLOW_NOT_APPLICABLE_CHIP, ALLOW_NOT_APPLICABLE_HELP } from "./not-applicable";
import { state } from "../state";
import { clientsView } from "../dom";
import { escapeHtml } from "../utils/html";
import type { ClientContextData, CarerSummary } from "../types";
import {
  renderConsolidatedChart,
  createTrajectoryCharts,
  getPlannedTrajectory,
} from "../charts/trajectory-chart";
import { CHART_COLORS } from "../charts/chart-kit";
import type { ActualObservation } from "../types";
import {
  monitoredChartHTML, createMonitoredCharts, isDiary, diaryTimelineAll,
} from "../charts/monitored-chart";
import { diaryTabLabel, planEventSummaryParts } from "../charts/monitored-chart-data";
import {
  restoreDraftState,
  confirmAndDiscardDraft,
} from "./draft";
import { editPlan, restorePlanVersion } from "./plan-builder";
// OMON-329: version history + restore.
import { loadPlanVersions, type PlanVersionInfo } from "../loaders/patients";
import { ensureCatalogue } from "../loaders/ensure";
import { defaultBuilderMonitoredEvents } from "./monitored";
import { renderCombinedNarrative } from "./narrative";
import { keptSection, planCopyBody, planCopyTitle } from "./plan-copy";
import type { FamilyContactSummary } from "./family-contacts";
import { renderClientsView } from "./view";
import { wireRotaFoldout } from "./rota-week";
import { go } from "../router";
import { partsOf, resolvePartPrompt } from "./event-parts";
import type { MonitoredEventInfo } from "../types";

/**
 * OMON-329: confirm a restore, naming the version.
 *
 * Built as an in-page modal on the estate's existing `.modal-overlay` /
 * `.modal-box` pattern, NOT `window.confirm`. A native dialog blocks the page
 * and, in this estate, freezes browser automation outright — so nothing here
 * should ever reach for one.
 *
 * Resolves false on Cancel, on the backdrop, and on Escape, so there is no way
 * to dismiss it that accidentally means yes.
 */
function confirmRestore(what: string): Promise<boolean> {
  return new Promise((resolve) => {
    const modal = document.createElement("div");
    modal.className = "modal-overlay";
    modal.innerHTML = `
      <div class="modal-box">
        <h3>Restore this version?</h3>
        <p style="font-size:15px;margin-bottom:8px;"><b>${escapeHtml(what)}</b></p>
        <p style="font-size:13px;color:#6b7280;">
          This creates a draft containing that version's goals and monitored
          events. Nothing is overwritten and no version is deleted — carers
          keep seeing the current plan until you publish the draft.
        </p>
        <div class="modal-actions">
          <button class="btn-secondary" data-act="cancel">Cancel</button>
          <button class="btn-primary" data-act="ok">Restore into draft</button>
        </div>
      </div>`;
    const done = (val: boolean) => {
      document.removeEventListener("keydown", onKey);
      modal.remove();
      resolve(val);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") done(false); };
    modal.addEventListener("click", (e) => {
      const t = e.target as HTMLElement;
      if (t === modal) return done(false); // backdrop
      const act = t.closest("[data-act]")?.getAttribute("data-act");
      if (act === "ok") done(true);
      else if (act === "cancel") done(false);
    });
    document.addEventListener("keydown", onKey);
    document.body.appendChild(modal);
    modal.querySelector<HTMLElement>('[data-act="ok"]')?.focus();
  });
}

/**
 * Shared print mechanism for both plan-copy flows: append a transient
 * .print-doc-region to <body>, print it (print CSS hides everything else),
 * remove it. Setting document.title is the only browser-portable way to
 * influence the suggested "Save as PDF" filename.
 */
function printTransientDoc(title: string, regionHtml: string) {
  const region = document.createElement("div");
  region.className = "print-doc-region";
  region.innerHTML = regionHtml;
  document.body.appendChild(region);

  const originalTitle = document.title;
  const beforePrint = () => {
    document.title = title;
    document.body.classList.add("printing-doc");
  };
  const afterPrint = () => {
    document.title = originalTitle;
    document.body.classList.remove("printing-doc");
    region.remove();
    window.removeEventListener("beforeprint", beforePrint);
    window.removeEventListener("afterprint", afterPrint);
  };
  window.addEventListener("beforeprint", beforePrint);
  window.addEventListener("afterprint", afterPrint);
  window.print();
}

// OMON-042, reworked 2026-08-05 (interim OMON-289): the live plan print is
// now a COMPOSED long-form document — client tile, plan card, then every
// tab's content in sequence — instead of a DOM snapshot of whichever tab
// happened to be active. The tab strip never prints, so nothing renders as
// a heading whose data is absent.
/** OMON-232: a multi-part event's per-part prompts, labelled (plan's own, else the fact's default). */
function partPromptsHtml(m: MonitoredEventInfo): string {
  return partsOf(state.catalogueData.get(m.odCode)).map((p) => {
    const text = resolvePartPrompt(p, m.componentPrompts);
    return `<p style="font-size:13px;color:#374151;margin-top:4px;white-space:pre-wrap;"><b style="font-weight:600;">${escapeHtml(p.label)}</b>${text ? ` — ${escapeHtml(text)}` : ""}</p>`;
  }).join("");
}

export function downloadPlanCopy(includeNarrative = true) {
  const patient = state.selectedPatient;
  if (!patient) return;

  const heroCard = document.querySelector(".ct-hero-bg");
  const planCard = document.querySelector(".ct-plancard");

  // Developmental goals — same renderer as the draft print, differentiated
  // only by content: the live plan supplies recorded actuals per goal
  // (state.patientActuals, keyed by measureCode) and the app's planned
  // trajectory (getPlannedTrajectory ramps legacy goals without a stored
  // _trajectory, exactly as the on-screen chart does).
  const goalItems = state.patientGoals.map((g) => {
    const scoreLine = [
      typeof g.targetScore === "number" ? `Target: ${g.targetScore}/5` : "",
      typeof g.currentScore === "number" ? `Assessment: ${g.currentScore}/5` : "",
      g.mandatory === true ? "🛑 Mandatory" : "",
    ].filter(Boolean).join(" · ");
    const actuals = g.measureCode ? state.patientActuals[g.measureCode] ?? [] : [];
    return `
      <div class="pd-item">
        <h3>${escapeHtml(g.description)}</h3>
        <p class="pd-sub">${escapeHtml([g.category, g.subcategory].filter(Boolean).join(" › "))}${scoreLine ? ` · ${scoreLine}` : ""}</p>
        ${g._smartGoal ? `<p class="pd-smart">${escapeHtml(g._smartGoal)}</p>` : ""}
        ${planChartSvg(getPlannedTrajectory(g), includeNarrative ? actuals : [])}
      </div>`;
  });

  // The remaining three tabs are pure string renders over already-loaded
  // state — data-complete regardless of which tab is on screen.
  const diaryEvents = state.patientMonitoredEvents.filter(isDiary);
  const monitoredEvents = state.patientMonitoredEvents.filter((m) => !isDiary(m));
  const monitoredItems = monitoredEvents.map((m) => {
    const chips = [
      m.mandatory === true ? "🛑 Mandatory" : "",
      m.escalate === true
        ? `⚠ Escalates${typeof m.escalationThreshold === "number" ? ` ${m.escalationDirection === "below" ? "<" : ">"} ${m.escalationThreshold}` : ""}` : "",
      typeof m.targetValue === "number" ? `Target ≤ ${m.targetValue}/shift` : "",
      m.allowNotApplicable === true ? ALLOW_NOT_APPLICABLE_CHIP : "",
    ].filter(Boolean).join(" · ");
    return `
      <div class="pd-item">
        <h3>${escapeHtml(m.title)}</h3>
        <p class="pd-sub">${escapeHtml(m.subcategory ?? "")}${chips ? `${m.subcategory ? " · " : ""}${chips}` : ""}</p>
        ${m.prompt ? `<p class="pd-smart">${escapeHtml(m.prompt)}</p>` : ""}
        ${includeNarrative ? monitoredChartHTML(m, state.patientNarrative) : ""}
      </div>`;
  });
  // Logs are plan content (Ed 2026-09-30, reversing 09-16): both copies list
  // each one with its prompt; only the full copy carries its entries. Plan-
  // only never builds the timeline, so no recorded text can reach it.
  const diaryItems: string[] = diaryEvents.map((m) => `
    <div class="pd-item">
      <h3>${escapeHtml(m.title)}</h3>
      ${m.prompt ? `<p class="pd-smart">${escapeHtml(m.prompt)}</p>` : ""}
      ${includeNarrative ? diaryTimelineAll(m.odCode, state.patientNarrative, m.recordingKind) : ""}
    </div>`);

  const dateStamp = new Date().toISOString().slice(0, 10);
  printTransientDoc(
    planCopyTitle(patient.name, dateStamp, includeNarrative),
    planCopyBody(
      {
        heroHtml: heroCard
          ? heroCard.outerHTML
          : `<h1 style="font-size:22px;margin-bottom:8px;">${escapeHtml(patient.name)} — Outcome Monitoring Plan</h1>`,
        planCardHtml: planCard ? planCard.outerHTML : "",
        goalItems,
        monitoredItems,
        diaryItems,
        // Rendered only when it will be used: the transcript is the most
        // expensive part of the document to build.
        narrativeHtml: includeNarrative ? renderCombinedNarrative() : "",
      },
      // Cover page only after the REAL client card — after the fallback name
      // heading it would leave page 1 holding nothing but a name.
      { includeNarrative, coverPage: Boolean(heroCard) },
    ),
  );
}

/**
 * The ONE trajectory chart for printed plan copies — a self-contained SVG
 * shared by the live and draft prints, differentiated only by content:
 * the live print passes recorded actuals, a draft passes none (it has no
 * recording history). SVG rather than a Chart.js canvas snapshot because
 * inline markup paints synchronously — data-URL <img>s race window.print()
 * and print blank. Colour semantics mirror chart-kit's CHART_COLORS
 * (green actual / blue has-note / red score-0 "attempted", OMON-275).
 */
function planChartSvg(traj: number[], actuals: ActualObservation[] = []): string {
  if (traj.length < 2) return "";
  const w = 560, h = 96, padL = 20, padR = 8, padT = 8, padB = 16;
  const x = (i: number) => padL + (i * (w - padL - padR)) / (traj.length - 1);
  const y = (v: number) => padT + (1 - Math.max(0, Math.min(5, v)) / 5) * (h - padT - padB);
  const grid = [0, 1, 2, 3, 4, 5].map((v) =>
    `<line x1="${padL}" y1="${y(v)}" x2="${w - padR}" y2="${y(v)}" stroke="#f3f4f6" stroke-width="1"/>` +
    `<text x="${padL - 4}" y="${y(v) + 3}" font-size="7" fill="#9ca3af" text-anchor="end">${v}</text>`,
  ).join("");
  const step = Math.max(1, Math.ceil(traj.length / 12));
  const ticks = traj
    .map((_, i) => i)
    .filter((i) => i % step === 0 || i === traj.length - 1)
    .map((i) => `<text x="${x(i).toFixed(1)}" y="${h - 4}" font-size="7" fill="#9ca3af" text-anchor="middle">M${i + 1}</text>`)
    .join("");
  const pts = traj.map((v, i) => `${x(i).toFixed(1)},${y(v).toFixed(1)}`).join(" ");

  // Actuals at their real date's fractional-month position (falling back to
  // the loader's integer monthIndex when the date doesn't parse).
  const planStart = new Date(state.planStartDate).getTime();
  const monthMs = 30.44 * 24 * 3600 * 1000;
  const dots = actuals.map((obs) => {
    const t = new Date(obs.date).getTime();
    const pos = Number.isFinite(t) && Number.isFinite(planStart)
      ? (t - planStart) / monthMs
      : obs.monthIndex;
    const i = Math.max(0, Math.min(traj.length - 1, pos));
    const fill = obs.score === 0
      ? CHART_COLORS.attempted
      : obs.note ? CHART_COLORS.actualNote : CHART_COLORS.actual;
    return `<circle cx="${x(i).toFixed(1)}" cy="${y(obs.score).toFixed(1)}" r="2.2" fill="${fill}"/>`;
  }).join("");

  const legend = actuals.length > 0
    ? `<text x="${w - padR}" y="${padT + 6}" font-size="8" text-anchor="end">` +
      `<tspan fill="${CHART_COLORS.planned}">Planned</tspan>` +
      `<tspan dx="8" fill="${CHART_COLORS.actual}">● Actual</tspan></text>`
    : `<text x="${w - padR}" y="${padT + 6}" font-size="8" fill="${CHART_COLORS.planned}" text-anchor="end">Planned</text>`;

  return `<svg class="pd-chart" viewBox="0 0 ${w} ${h}" width="${w}" height="${h}" xmlns="http://www.w3.org/2000/svg">
    ${grid}${ticks}
    <polyline points="${pts}" fill="none" stroke="${CHART_COLORS.planned}" stroke-width="1.5" stroke-dasharray="4 3"/>
    ${dots}
    ${legend}
  </svg>`;
}

/**
 * Print a copy of the in-flight DRAFT. A draft has no rendered document —
 * it's serialised builder state on the draft _carePlan doc (`_draftState`),
 * with no _goal docs and no observations — so this builds a transient
 * print-only region (goals + monitored events, clearly badged DRAFT),
 * prints it, and removes it. The parse here is deliberately LOCAL:
 * restoreDraftState() writes into the live builder state, which must not
 * be touched by a read-only print.
 */
export function printDraftCopy() {
  const patient = state.selectedPatient;
  const draft = state.patientDraft;
  if (!patient || !draft?._draftState) return;

  let goals: Array<Record<string, unknown>> = [];
  let events: Array<Record<string, unknown>> = [];
  let planMonths = 12;
  let planStartDate = "";
  try {
    const parsed = JSON.parse(draft._draftState);
    if (Array.isArray(parsed.builderGoals)) goals = parsed.builderGoals;
    if (Array.isArray(parsed.builderMonitoredEvents)) events = parsed.builderMonitoredEvents;
    if (typeof parsed.planMonths === "number") planMonths = parsed.planMonths;
    if (typeof parsed.planStartDate === "string") planStartDate = parsed.planStartDate;
  } catch (err) {
    console.error("Could not parse draft state for printing:", err);
    alert("This draft could not be read for printing.");
    return;
  }

  const draftVersion = state.patientCarePlans.length > 0
    ? (state.patientCarePlans[0]._version || 1) + 1
    : 1;
  const lastSaved = draft._draftLastSavedAt
    ? new Date(draft._draftLastSavedAt).toLocaleString("en-GB")
    : "unknown";

  const str = (v: unknown): string => (typeof v === "string" ? v : "");
  const num = (v: unknown): number | undefined => (typeof v === "number" ? v : undefined);

  // Mirrors the live print's per-goal reading (title, Target/Assessment,
  // SMART text, trajectory chart). Carer guidance is deliberately omitted —
  // the live print doesn't include it either, and it's pages long per goal.
  const goalRows = goals.map((g) => {
    const trajectory = Array.isArray(g.trajectory)
      ? (g.trajectory as unknown[]).filter((t): t is number => typeof t === "number")
      : [];
    const scoreLine = [
      num(g.targetScore) !== undefined ? `Target: ${g.targetScore}/5` : "",
      num(g.currentScore) !== undefined ? `Assessment: ${g.currentScore}/5` : "",
      num(g.baselineMonths) !== undefined ? `baseline ${g.baselineMonths}mo` : "",
      g.mandatory === true ? "🛑 Mandatory" : "",
    ].filter(Boolean).join(" · ");
    return `
      <div class="pd-item">
        <h3>${escapeHtml(str(g.title) || str(g.name))}</h3>
        <p class="pd-sub">${escapeHtml([str(g.goalCategory), str(g.subcategory)].filter(Boolean).join(" › "))}${scoreLine ? ` · ${scoreLine}` : ""}</p>
        ${str(g.smartGoal) ? `<p class="pd-smart">${escapeHtml(str(g.smartGoal))}</p>` : ""}
        ${planChartSvg(trajectory)}
      </div>`;
  });

  const eventRows = events.map((m) => {
    const chips = [
      m.mandatory === true ? "🛑 Mandatory" : "",
      m.escalate === true
        ? `⚠ Escalates${num(m.escalationThreshold) !== undefined ? ` ${m.escalationDirection === "below" ? "<" : ">"} ${m.escalationThreshold}` : ""}` : "",
      num(m.targetValue) !== undefined ? `Target ≤ ${m.targetValue}/shift` : "",
      // ADR-0035 am.9a (the draft JSON carries the builder field).
      m.allowNotApplicable === true ? ALLOW_NOT_APPLICABLE_CHIP : "",
    ].filter(Boolean).join(" · ");
    return `
      <div class="pd-item">
        <h3>${escapeHtml(str(m.title) || str(m.odCode))}</h3>
        <p class="pd-sub">${escapeHtml(str(m.subcategory))}${chips ? `${str(m.subcategory) ? " · " : ""}${chips}` : ""}</p>
        ${str(m.prompt) ? `<p class="pd-smart">${escapeHtml(str(m.prompt))}</p>` : ""}
      </div>`;
  });

  // The client-details tile: clone the hero card the detail view has
  // already rendered (avatar included — its blob URL stays valid within
  // this document) so the draft print's first page matches the live
  // print's. Falls back to a plain name heading if the card isn't there.
  const heroCard = document.querySelector(".ct-hero-bg");
  const heroHtml = heroCard ? heroCard.outerHTML : "";

  const dateStamp = new Date().toISOString().slice(0, 10);
  printTransientDoc(`${patient.name} — SOMA Plan DRAFT v${draftVersion} — ${dateStamp}`, `
    ${heroHtml || `<h1 style="font-size:22px;margin-bottom:2px;">${escapeHtml(patient.name)} — Outcome Monitoring Plan</h1>`}
    <div class="${heroHtml ? "pd-plan pd-plan-start" : "pd-plan"}">
    <p style="font-size:15px;color:#b45309;font-weight:600;margin-bottom:2px;">DRAFT v${draftVersion} — not yet live · last saved ${escapeHtml(lastSaved)}</p>
    <p style="font-size:13px;color:#6b7280;margin-bottom:12px;">
      ${planStartDate ? `Planned start ${escapeHtml(planStartDate)} · ` : ""}${planMonths}-month plan ·
      carers see the live plan until this draft is published.
    </p>
    ${goalRows.length ? keptSection(`Developmental goals (${goals.length})`, goalRows) : ""}
    ${eventRows.length ? keptSection(`Monitored events (${events.length})`, eventRows) : ""}
    ${!goalRows.length && !eventRows.length ? `<p>This draft is empty.</p>` : ""}
    </div>
  `);
}

/**
 * The ⬇ button's entry point. With only one printable version the choice
 * is made for the user; with a live plan AND a draft in flight, ask which
 * one to print (mirrors the publish/discard modal pattern).
 */
export function openPrintChooser() {
  const plan = state.patientCarePlans[0];
  const draft = state.patientDraft;
  // A draft has no recording history at all, so "plan only" is the only copy
  // it could ever produce — no question to ask.
  if (!plan && draft) { printDraftCopy(); return; }
  if (!plan) return;

  const liveVersion = plan._version || 1;
  const modal = document.createElement("div");
  modal.className = "modal-overlay";
  modal.id = "__printChooserModal";
  // ONE question where there used to be none, and two where a draft exists:
  // what to include (Ed, 2026-09-16, for a business user who wants a plan
  // they can hand over without a client's whole recording history attached),
  // and — only if there is a draft — which version.
  modal.innerHTML = `
    <div class="modal-box">
      <h3>What should the copy include?</h3>
      <p>The full copy carries every recording and note made against
         v${liveVersion} of this plan. Plan only prints the plan itself:
         goals with their planned trajectory, monitored events and what
         carers record, with no recorded data, so it can be shared with a
         GP or social worker.${
           draft ? ` This client also has a draft in progress; carers see
         the live v${liveVersion} plan until it is published.` : ""}</p>
      <div class="modal-actions">
        <button class="btn-secondary" id="__printCancel">Cancel</button>
        ${draft ? `<button class="btn-secondary" id="__printDraft">Draft v${liveVersion + 1}</button>` : ""}
        <button class="btn-secondary" id="__printPlanOnly">Plan only</button>
        <button class="btn-amber" id="__printFull">Plan + narrative</button>
      </div>
    </div>
  `;
  document.body.appendChild(modal);
  const close = () => modal.remove();
  modal.querySelector("#__printCancel")?.addEventListener("click", close);
  modal.addEventListener("click", (e) => { if (e.target === modal) close(); });
  modal.querySelector("#__printFull")?.addEventListener("click", () => { close(); downloadPlanCopy(true); });
  modal.querySelector("#__printPlanOnly")?.addEventListener("click", () => { close(); downloadPlanCopy(false); });
  modal.querySelector("#__printDraft")?.addEventListener("click", () => { close(); printDraftCopy(); });
}

// ======================================================================
// OMON-149: client-context header rendering helpers.
//
// Pure-string-builders that take the loaded ClientContextData (plus
// the plan's period.start for the plan-start-date line) and produce
// HTML strings. Kept here as module-internal helpers because they
// only ever feed into renderPatientDetail's header block — pulling
// them into a separate file would be overkill for ~60 LOC.
// ======================================================================

/**
 * Format an ISO date string as "15 May 2026" — matches the mockup
 * Ed shared. Returns the raw string unchanged if Date() can't parse it.
 */
function formatDateLong(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  const months = ["January", "February", "March", "April", "May", "June",
                  "July", "August", "September", "October", "November", "December"];
  return `${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear()}`;
}

/** Render the comma-joined carer list, or undefined if no carers. */
function joinCarerNames(carers: CarerSummary[]): string | undefined {
  if (carers.length === 0) return undefined;
  return carers.map((c) => escapeHtml(c.name)).join(", ");
}

/**
 * Build the four OMON-149 header lines as HTML strings, returned as
 * one concatenated block. Empty sub-lines are omitted (not rendered
 * as empty <p> tags) so the header stays compact when data is sparse.
 *
 * Empty-state policies per the ticket:
 *   - no birthDate → "Age unknown" (still render the line, so the
 *     planner sees the system tried but didn't find data)
 *   - no address summary → omit "·" + address from the line
 *   - no carers → "Carers assigned: (none scheduled)"
 *   - no today's carers → "Working today: (no active shift today)"
 */

/**
 * MONO-106: circular avatar — photo when a `photo[0]` Attachment exists
 * (hydrated post-render via the authenticated Storage SDK so reads are
 * rules-gated, not tokenised), monogram fallback always rendered first.
 */
function renderAvatar(name: string, path: string | undefined, px: number, id?: string): string {
  const initials = name.split(/\s+/).map((w) => w[0] ?? "").join("").slice(0, 2).toUpperCase() || "?";
  return `<span ${id ? `id="${id}"` : ""} ${path ? `data-avatar-path="${escapeHtml(path)}"` : ""}
    style="width:${px}px;height:${px}px;border-radius:50%;flex:none;display:inline-grid;place-items:center;
           background:linear-gradient(180deg,#9ca3af,#6b7280);color:#fff;overflow:hidden;
           font-size:${Math.round(px * 0.38)}px;font-weight:600;">${escapeHtml(initials)}</span>`;
}

/**
 * Storage path → object URL, for the lifetime of the document.
 *
 * ☠️ Without this the tile visibly FLASHES on every render: the monogram
 * paints, then the photo is re-fetched from Storage and swaps in. Switching
 * plan tabs re-renders the header, so a planner saw initials blink to photo on
 * every single tab click (Ed, 2026-09-03).
 *
 * Keyed by PATH, not by client, which makes replacement self-correcting: a new
 * upload lands under a new `_mediaId`, so the path changes, so it misses the
 * cache and re-fetches. No invalidation logic to get wrong.
 *
 * The URLs are deliberately never revoked. They are a handful of small images
 * per session, and revoking one that another render still references would
 * reintroduce the very flash this removes.
 */
const avatarBlobUrls = new Map<string, string>();

function avatarImg(url: string): string {
  return `<img src="${url}" style="width:100%;height:100%;object-fit:cover;" alt="">`;
}

/** Hydrate every data-avatar-path element via rules-gated Storage reads. */
function hydrateAvatars(root: HTMLElement): void {
  root.querySelectorAll<HTMLElement>("[data-avatar-path]").forEach(async (el) => {
    const path = el.getAttribute("data-avatar-path")!;
    // ☠️ Must run BEFORE the first `await`. An async function body is
    // synchronous up to its first await, so a cache hit swaps the photo in
    // within the same task and the browser never paints the monogram. Move
    // this below an await and the flash comes straight back.
    const cached = avatarBlobUrls.get(path);
    if (cached) {
      el.innerHTML = avatarImg(cached);
      return;
    }
    try {
      const { getBlob, ref } = await import("firebase/storage");
      const { storage } = await import("../firebase");
      const blob = await getBlob(ref(storage, path));
      const url = URL.createObjectURL(blob);
      avatarBlobUrls.set(path, url);
      el.innerHTML = avatarImg(url);
    } catch (err) {
      console.warn("avatar hydrate failed", err); // monogram remains — fine
    }
  });
}

/**
 * MONO-106: admin/planner avatar upload. Sends the file to the ADR-0031
 * quarantine (`uploads/{uid}/{uuid}` + owner metadata); agent.media
 * validates, strips EXIF, writes photo[0] + audit. We poll the patient doc
 * briefly so the tile swaps monogram → photo without a manual refresh.
 */
function wireAvatarUpload(root: HTMLElement): void {
  const el = root.querySelector<HTMLElement>("#clientAvatar");
  const patientDocId = state.clientContext?.patientDocId;
  if (!el || !patientDocId) return;
  el.style.cursor = "pointer";
  el.title = "Set client photo";
  el.addEventListener("click", () => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/jpeg,image/png,image/webp";
    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;
      const { ref, uploadBytes } = await import("firebase/storage");
      const { storage, auth } = await import("../firebase");
      const uid = auth.currentUser?.uid;
      if (!uid) return;
      const uploadId = crypto.randomUUID();
      el.style.opacity = "0.5";
      try {
        await uploadBytes(ref(storage, `uploads/${uid}/${uploadId}`), file, {
          contentType: file.type,
          customMetadata: { ownerCollection: "_patient", ownerDocId: patientDocId },
        });
        // Poll for the CF's write (usually <5s), then re-render the detail
        // view so the loader picks up photo[0].
        const { getDoc, doc: fsDoc } = await import("firebase/firestore");
        const { db } = await import("../firebase");
        for (let i = 0; i < 10; i++) {
          await new Promise((r) => setTimeout(r, 1500));
          const snap = await getDoc(fsDoc(db, "_patient", patientDocId));
          const photos = (snap.data() as { photo?: unknown[] } | undefined)?.photo;
          if (Array.isArray(photos) && photos.length > 0) break;
        }
        if (state.selectedPatient) {
          const { loadClientContext } = await import("../loaders/client-context");
          state.clientContext = await loadClientContext(state.selectedPatient);
        }
        renderPatientDetail();
      } catch (err) {
        console.warn("avatar upload failed", err);
        el.style.opacity = "1";
      }
    };
    input.click();
  });
}

/**
 * ONE-015: the Family & contacts column of the client-information tile.
 * Renders the client's `_relatedPerson` mirrors (OneTouch-canonical —
 * read-only here; fixes happen in OneTouch and sync down twice daily).
 * Access pills — ☠️ ADR-0045 amendment 2026-09-21: the office is the only
 * gate. These used to read "◍ ready" for any contact with a number on file,
 * because the sign-in admitted any matching number; from that date a number
 * is not access. See `accessState()` in family-contacts.ts.
 */
const ACCESS_PILLS: Record<FamilyContactSummary["accessState"], [string, string]> = {
  "using": ["● app", "ct-pill-bound"],
  "granted": ["◍ access given", "ct-pill-await"],
  "not-granted": ["○ no access", "ct-pill-nonum"],
  // 2026-10-24: they signed in to the app and asked — the office's turn.
  "requested": ["◐ asked to join", "ct-pill-ask"],
  "revoked": ["○ access removed", "ct-pill-nonum"],
};

function renderFamilyContacts(ctx: ClientContextData | null): string {
  const contacts = ctx?.familyContacts ?? [];
  const rows = contacts.map((c, i) => {
    // OMON-397: a revoked contact who signed in again — the office's turn again.
    const [pillText, pillClass] = c.accessState === "revoked" && c.reaskedAt
      ? ["◐ removed · asked again", "ct-pill-ask"] : ACCESS_PILLS[c.accessState];
    return `
    <div class="ct-rowi ct-click" data-contact-idx="${i}">
      <div class="ct-grow">
        <span class="ct-lbl">${escapeHtml(c.relationships.join(" · ")) || "contact"}</span>
        <span class="ct-val">${escapeHtml(c.name)}${c.phone ? ` · 📱 ${escapeHtml(c.phone)}` : " · no number"}</span>
      </div>
      <span class="ct-pill ${pillClass}">${pillText}</span><span class="ct-chev">›</span>
    </div>`;
  }).join("");
  return `
    <div class="ct-group">
      <div class="ct-group-title">Family &amp; contacts <span class="ct-title-note">· from OneTouch</span></div>
      ${rows || `<div class="ct-rowi"><div class="ct-grow"><span class="ct-val" style="color:rgba(60,60,67,.62);">No contacts synced from OneTouch yet.</span></div></div>`}
      <div class="ct-hint">add or fix contacts in OneTouch — they sync here twice daily. App access is given here, by the office.</div>
    </div>`;
}

/**
 * The vertical Contacts card for one family contact (the mock's popup).
 * Read-only projection of the ONE-015 mirror — the foot line restates the
 * canonicity rule so nobody hunts for an edit button that shouldn't exist.
 */
const ACCESS_EXPLANATIONS: Record<FamilyContactSummary["accessState"], string> = {
  "using": "● Using the Circle of Support app",
  "granted": "◍ Access given — they can sign in to the app; they haven't yet",
  "not-granted": "○ No access. Having their details on file does not let them in — the office decides.",
  "requested": "◐ Asked to join — they signed in to the app with this mobile number and are waiting for the office.",
  "revoked": "○ Access removed by the office",
};

/** OMON-397: "Access removed by the office — they signed in again on Thu 29 Oct,
 * 11:20 and were refused. Give access again to let them in." */
export function reaskedLine(c: FamilyContactSummary): string {
  const when = c.reaskedAt ? new Date(c.reaskedAt).toLocaleString("en-GB", {
    weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit",
  }) : "";
  return `○ Access removed by the office — they signed in again${when ? ` on ${when}` : ""} `
    + "and were refused. Give access again to let them in.";
}

/** "Asked to join on Sat 24 Oct, 13:42" when the request carries a time. */
function requestedLine(c: FamilyContactSummary): string {
  if (!c.requestedAt) return ACCESS_EXPLANATIONS.requested;
  const when = new Date(c.requestedAt).toLocaleString("en-GB", {
    weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit",
  });
  return `◐ Asked to join on ${when} — they signed in to the app with this mobile number and are waiting for the office.`;
}

/** Re-read the client after an office action so the card and pills are true. */
async function reloadClientAfterAction(): Promise<void> {
  if (state.selectedPatient) {
    const { loadClientContext } = await import("../loaders/client-context");
    state.clientContext = await loadClientContext(state.selectedPatient);
  }
  renderPatientDetail();
}

function openContactCard(c: FamilyContactSummary): void {
  const scrim = document.getElementById("ctScrim");
  if (!scrim) return;
  const rows: [string, string][] = [
    ["mobile", c.phone ?? "— none in OneTouch"],
    ["email", c.email ?? "—"],
    ["address", c.address ?? "—"],
    ["app access", c.accessState === "requested" ? requestedLine(c)
      : c.accessState === "revoked" && c.reaskedAt ? reaskedLine(c) : ACCESS_EXPLANATIONS[c.accessState]],
  ];
  const canGrant = !!c.docId && (c.accessState === "not-granted" || c.accessState === "revoked"
    || c.accessState === "requested");
  const canRevoke = !!c.docId && (c.accessState === "using" || c.accessState === "granted");
  // "Give access" decides before anyone asked; "Approve their request"
  // answers someone who did — and only that one tells them they're in.
  const grantLabel = c.accessState === "revoked" ? "Give access again"
    : c.accessState === "requested" ? "Approve their request"
    : "Give Circle of Support access";
  // Sign-in today is by phone; email sign-in is coming (ADR-0045 amendment
  // 2026-09-21). A grant without a number is still meaningful — say what is
  // missing rather than hide the button.
  const noNumberNote = canGrant && !c.phone
    ? `<div class="ct-hint">No mobile on file — they'll need one to sign in. Add it in OneTouch.</div>`
    : "";
  scrim.innerHTML = `
    <div class="ct-ccard">
      <button class="ct-close" id="ctClose">✕</button>
      ${renderAvatar(c.name, c.avatarPath, 96)}
      <h2 class="ct-who-name" style="font-size:22px;">${escapeHtml(c.name)}</h2>
      <div class="ct-who-sub">${escapeHtml(c.relationships.join(" · ")) || "contact"}</div>
      <div class="ct-list">
        ${rows.map(([l, v]) => `
          <div class="ct-rowi"><div class="ct-grow">
            <span class="ct-lbl">${escapeHtml(l)}</span><span class="ct-val">${escapeHtml(v)}</span>
          </div></div>`).join("")}
      </div>
      ${canGrant
        ? `<button class="btn-primary" id="ctGrantBtn" style="margin-top:12px;">${grantLabel}</button>${noNumberNote}`
        : ""}
      ${canRevoke
        ? `<button class="btn-secondary" id="ctRevokeBtn" style="margin-top:12px;">Remove app access</button>`
        : ""}
      <div class="ct-foot">details come from OneTouch — edit there and they sync within the day</div>
    </div>`;
  scrim.classList.add("ct-open");
  scrim.querySelector("#ctClose")?.addEventListener("click", closeContactCard);
  // OMON-208 slice 4: withdraw app access. Identity is NOT touched — they
  // remain a contact of the client (OneTouch is canonical for that); only
  // the STC-owned access state changes, and the mirror is flagged so the
  // next sign-in cannot silently re-bind on the still-matching number.
  scrim.querySelector("#ctRevokeBtn")?.addEventListener("click", async () => {
    if (!confirm(
      `Remove ${c.name}'s access to the Circle of Support app?\n\n` +
      `They stay on the client's contact list — only their app access is ` +
      `withdrawn, and it stops immediately. To restore it later, open this ` +
      `contact again and choose "Give access again".`,
    )) return;
    const btn = scrim.querySelector<HTMLButtonElement>("#ctRevokeBtn");
    if (btn) { btn.disabled = true; btn.textContent = "Removing…"; }
    try {
      const { callRelatedPersonAction } = await import("../admin/cf-client");
      await callRelatedPersonAction({ action: "revoke", relatedPersonDocId: c.docId! });
      closeContactCard();
      await reloadClientAfterAction();
    } catch (err) {
      if (btn) { btn.disabled = false; btn.textContent = "Remove app access"; }
      alert(`Could not remove access: ${(err as Error).message}`);
    }
  });
  // ADR-0045 amendment 2026-09-21: the office's yes. Only the office can
  // give access — a number or email on file does not.
  scrim.querySelector("#ctGrantBtn")?.addEventListener("click", async () => {
    if (!confirm(
      (c.accessState === "requested"
        ? `Approve ${c.name}'s request to use the Circle of Support app for this client?\n\n` +
          `They asked from the app with the mobile number on this contact; ` +
          `they'll be told they're in. `
        : `Give ${c.name} access to the Circle of Support app for this client?\n\n`) +
      `They will be able to sign in and see this client's care. You can ` +
      `remove access again at any time from this contact.`,
    )) return;
    const btn = scrim.querySelector<HTMLButtonElement>("#ctGrantBtn");
    if (btn) { btn.disabled = true; btn.textContent = "Giving access…"; }
    try {
      const { callRelatedPersonAction } = await import("../admin/cf-client");
      // No requestId on purpose: the server finds every pending request
      // naming this contact and closes each once all its contacts have
      // access — passing one would close a multi-client request early.
      await callRelatedPersonAction({ action: "grant", relatedPersonDocId: c.docId! });
      closeContactCard();
      await reloadClientAfterAction();
    } catch (err) {
      if (btn) { btn.disabled = false; btn.textContent = grantLabel; }
      alert(`Could not give access: ${(err as Error).message}`);
    }
  });
  hydrateAvatars(scrim);
}

function closeContactCard(): void {
  const scrim = document.getElementById("ctScrim");
  if (scrim) { scrim.classList.remove("ct-open"); scrim.innerHTML = ""; }
}

/** Document-level Escape-to-close is wired once, not per render. */
let escCloseWired = false;

/**
 * The Care group of the tile — the same facts renderClientContextLines
 * used to print as prose, recast as inset-grouped label-over-value rows
 * (the Apple-restyle mock's left group). Empty-states preserved so the
 * planner never wonders whether a blank is a loading bug.
 */
function renderCareGroup(
  ctx: ClientContextData | null,
  planStart: string | undefined,
): string {
  const row = (lbl: string, val: string) => `
    <div class="ct-rowi"><div class="ct-grow">
      <span class="ct-lbl">${escapeHtml(lbl)}</span><span class="ct-val">${val}</span>
    </div></div>`;
  const dim = (text: string) => `<em style="color:rgba(60,60,67,.62);">${escapeHtml(text)}</em>`;
  const rows: string[] = [];
  if (ctx) {
    // Practice — most common `area` across assigned carers, from
    // `_practitioner.extension.onetouch-organisation.area`.
    rows.push(row("practice", ctx.practice ? escapeHtml(ctx.practice) : dim("unknown")));
    // Care Manager — `EoC.careManager[0].reference`; manually-seeded pilots
    // populate this, the ONE-004 cohort doesn't (OMON-158).
    rows.push(row("care manager", ctx.careManager ? escapeHtml(ctx.careManager) : dim("not assigned")));
    rows.push(row("carers assigned", joinCarerNames(ctx.plannedCarers) ?? dim("none scheduled")));
    rows.push(row("working today", joinCarerNames(ctx.todaysCarers) ?? dim("no active shift today")));
  }
  if (planStart) {
    rows.push(row("plan start", escapeHtml(formatDateLong(planStart))));
  }
  return `
    <div class="ct-group">
      <div class="ct-group-title">Care</div>
      ${rows.join("") || `<div class="ct-rowi"><div class="ct-grow"><span class="ct-val">${dim("no care context loaded")}</span></div></div>`}
    </div>`;
}

// ======================================================================
// Patient Detail — shows plan with consolidated chart
// ======================================================================

export function renderPatientDetail() {
  // Destroy any existing Chart.js instances before re-rendering DOM
  for (const c of state.activeCharts) c.destroy();
  state.activeCharts = [];

  if (!state.selectedPatient) return;
  const p = state.selectedPatient;
  const plan = state.patientCarePlans[0]; // primary plan

  // Load plan duration from CarePlan.period for chart rendering
  if (plan?.periodStart) {
    state.planStartDate = plan.periodStart;
    if (plan.periodEnd) {
      const start = new Date(plan.periodStart);
      const end = new Date(plan.periodEnd);
      state.planMonths = Math.max(3, (end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth()));
    }
  }

  // OMON-035: build the "Draft in progress" banner first — it surfaces
  // anywhere a draft exists, regardless of whether a live plan also exists.
  let draftResumeBanner = "";
  if (state.patientDraft) {
    const draftVersion = state.patientCarePlans.length > 0
      ? (state.patientCarePlans[0]._version || 1) + 1
      : 1;
    const liveStatus = state.patientCarePlans.length > 0
      ? `the live v${state.patientCarePlans[0]._version || 1} plan`
      : "no plan";
    const lastSaved = state.patientDraft._draftLastSavedAt
      ? new Date(state.patientDraft._draftLastSavedAt).toLocaleString("en-GB")
      : "unknown";
    draftResumeBanner = `
      <div class="draft-resume-banner">
        <div>
          <strong>Draft v${draftVersion} in progress</strong>
          <p>Last saved ${escapeHtml(lastSaved)}. Carers see ${escapeHtml(liveStatus)} until you click ‘Save Live Version’ in the plan editor.</p>
        </div>
        <div class="draft-resume-actions">
          <button class="btn-secondary" id="discardDraftBtn">Discard draft</button>
          <button class="btn-amber" id="resumeDraftBtn">Resume editing</button>
        </div>
      </div>
    `;
  }

  let planHtml: string;
  if (!plan && !state.patientDraft) {
    planHtml = `<div class="empty-state">
      <h3>No SOMA plan yet</h3>
      <p>Create an outcome monitoring plan for this client.</p>
      <button class="btn-primary" id="createPlanBtn">Create plan</button>
    </div>`;
  } else if (!plan && state.patientDraft) {
    // Draft-only state: the banner above tells the whole story.
    planHtml = "";
  } else {
    const goalCount = state.patientGoals.length;
    // OMON-121: summary line on the plan card now reflects both streams.
    // Ed 2026-09-30: logs (diary-kind) counted apart, as the tabs split them.
    const summaryParts: string[] = [];
    summaryParts.push(`${goalCount} goal${goalCount === 1 ? "" : "s"}`);
    summaryParts.push(...planEventSummaryParts(state.patientMonitoredEvents));
    summaryParts.push(`created ${plan.created ? plan.created.slice(0, 10) : "unknown"}`);
    // ☠️ No baseline-assessment count here (Ed, 2026-09-02). It read as an
    // outstanding-work tally on a card that is otherwise a plain description
    // of the plan — "11 awaiting assessment" looks like a queue somebody has
    // to clear, when an unfixed baseline is simply a goal whose starting
    // point has not been pinned yet. Its "all assessments set" twin went with
    // it: a card that sometimes congratulates and sometimes chases is not
    // reporting, it is nagging.
    //
    // The baseline state itself is unchanged and still lives on each goal
    // (`_baselineFixed`); only this summary line is gone.
    planHtml = `
      <div class="card ct-plancard" style="margin-bottom:16px;">
        <div style="display:flex;justify-content:space-between;align-items:center;">
          <div>
            <h3 style="font-size:15px;margin-bottom:2px;">Outcome Monitoring Plan</h3>
            <p style="font-size:13px;color:#6b7280;">${summaryParts.join(" &middot; ")}</p>
          </div>
          <div style="display:flex;gap:8px;align-items:center;">
            <span class="badge badge-version">v${plan._version || 1}</span>
            <span class="badge badge-${plan.status === "active" ? "active" : "draft"}">${plan.status}</span>
            <button class="btn-secondary" id="planHistoryBtn">Version history</button>
            ${state.patientDraft ? "" : `<button class="btn-secondary" id="editPlanBtn">Create new draft</button>`}
          </div>
        </div>
        <!-- OMON-329: filled on demand by the Version history button. Every
             version is retained already; this is the affordance that was
             missing, so recovering an ordinary mistake no longer needs an
             engineer reading Firestore by hand. -->
        <div id="planHistoryPanel" hidden style="margin-top:12px;"></div>
      </div>`;
  }

  // OMON-121: tab toggle between Developmental goals and Tasks (monitored
  // events). The trajectory chart is now embedded INSIDE each goal row
  // (per the OMON-121 redesign) rather than living as a separate section.
  // Tasks tab shows monitored events as similar rich rows but without a
  // chart, since the recording modalities (binary / scalar / text) are
  // still being settled — keeping the views separate lets each evolve
  // without affecting the other.
  let tabsHtml = "";
  let tabContentHtml = "";
  if (plan) {
    const goalCount = state.patientGoals.length;
    // OMON-205 f/u: the diary is promoted OUT of the monitored-event set and
    // given its own tab. Split on recordingKind, never on the daily-log id.
    // It appears in exactly one place — leaving it in both tabs would show the
    // same records twice under two headings, which is the confusion this
    // promotion exists to remove.
    const diaryEvents = state.patientMonitoredEvents.filter(isDiary);
    const monitoredEvents = state.patientMonitoredEvents.filter((m) => !isDiary(m));
    const meCount = monitoredEvents.length;
    const activeTab = state.detailActiveTab;
    const devActive = activeTab === "developmental";
    const tasksActive = activeTab === "tasks";
    const dailyLogActive = activeTab === "dailylog";
    // OMON-129: the Combined-narrative transcript tab (decision Ed 2026-06-10).
    const narrativeActive = activeTab === "narrative";
    tabsHtml = `
      <div class="detail-tabs" style="display:flex;gap:8px;margin:16px 0 12px 0;">
        <button class="detail-tab ${devActive ? "detail-tab-active" : ""}" data-detail-tab="developmental"
                style="padding:8px 16px;border-radius:6px 6px 0 0;border:1px solid #d1d5db;border-bottom:${devActive ? "3px solid #d97706;background:#fff;font-weight:600;" : "3px solid transparent;background:#f9fafb;color:#6b7280;"};cursor:pointer;">
          Developmental goals <span style="color:#6b7280;font-weight:normal;">&middot; ${goalCount}</span>
        </button>
        <button class="detail-tab ${tasksActive ? "detail-tab-active" : ""}" data-detail-tab="tasks"
                style="padding:8px 16px;border-radius:6px 6px 0 0;border:1px solid #d1d5db;border-bottom:${tasksActive ? "3px solid #d97706;background:#fff;font-weight:600;" : "3px solid transparent;background:#f9fafb;color:#6b7280;"};cursor:pointer;">
          Monitored events <span style="color:#6b7280;font-weight:normal;">&middot; ${meCount}</span>
        </button>
        <button class="detail-tab ${narrativeActive ? "detail-tab-active" : ""}" data-detail-tab="narrative"
                style="padding:8px 16px;border-radius:6px 6px 0 0;border:1px solid #d1d5db;border-bottom:${narrativeActive ? "3px solid #991b1b;background:#fff;font-weight:600;" : "3px solid transparent;background:#f9fafb;color:#6b7280;"};cursor:pointer;">
          Combined narrative
        </button>
        ${diaryEvents.length > 0 ? `
        <button class="detail-tab ${dailyLogActive ? "detail-tab-active" : ""}" data-detail-tab="dailylog"
                style="padding:8px 16px;border-radius:6px 6px 0 0;border:1px solid #d1d5db;border-bottom:${dailyLogActive ? "3px solid #991b1b;background:#fff;font-weight:600;" : "3px solid transparent;background:#f9fafb;color:#6b7280;"};cursor:pointer;">
          ${(() => {
            const label = diaryTabLabel(state.patientMonitoredEvents);
            return escapeHtml(label.text) + (label.count == null ? ""
              : ` <span style="color:#6b7280;font-weight:normal;">&middot; ${label.count}</span>`);
          })()}
        </button>` : ""}
      </div>
    `;

    if (devActive) {
      // Developmental goals tab — merged rows with chart embedded.
      if (goalCount > 0) {
        tabContentHtml = `
          <div class="trajectory-panel">
            <div class="panel-header">
              <div style="font-size:13px;color:#6b7280;">${state.planMonths}-month trajectory &middot; showing today &plusmn;1 month</div>
              <span class="chart-legend">
                <span class="legend-plan">Planned</span>
                <span class="legend-actual">Actual</span>
                <span class="legend-note">Has notes</span>
              </span>
            </div>
            <div class="chart-zoom-hint" style="margin-bottom:8px;display:flex;align-items:center;gap:8px;flex-wrap:wrap;">
              <span>Ctrl+scroll or pinch to zoom out for the whole plan &middot; shift+drag to pan &middot; hover for detail</span>
              <span id="trajectoryRange" class="chart-range-badge" hidden></span>
              <button id="trajectoryResetZoom" class="chart-range-reset" type="button" hidden>Back to today</button>
            </div>
            ${renderConsolidatedChart(state.patientGoals)}
          </div>`;
      } else {
        tabContentHtml = `<div class="empty-state" style="padding:24px;">
          <h3>No developmental goals on this plan yet</h3>
          <p>Add some via Create new draft &rarr; Add Goals.</p>
        </div>`;
      }
    } else if (tasksActive) {
      // Tasks tab — monitored events as rich rows, each with a per-regime
      // review swimlane (replaces the ADR-0022 "visualisation pending"
      // placeholder, Ed 2026-06-14). The chart is a self-contained SVG
      // read-projection over state.patientNarrative, dispatched on
      // recordingKind — see charts/monitored-swimlane.ts.
      if (meCount > 0) {
        const rows = monitoredEvents.map((m) => `
          <div class="card goal-card" style="border-left:3px solid #d97706;">
            <h3>${escapeHtml(m.title)}</h3>
            <p style="font-size:13px;color:#6b7280;">
              ${m.subcategory ? escapeHtml(m.subcategory) : ""}
              ${m.dueDate ? ` &middot; Review by: ${escapeHtml(m.dueDate)}` : ""}
              ${typeof m.targetMeasure === "number" ? ` &middot; Target measure: ${m.targetMeasure}` : ""}
            </p>
            ${
              // OMON-174: the plan's prompting text (entry snapshot; loader
              // falls back to the live canonical for pre-OMON-174 entries).
              m.prompt ? `<p style="font-size:13px;color:#374151;margin-top:4px;white-space:pre-wrap;">${escapeHtml(m.prompt)}</p>` : ""}
            <div style="margin-top:6px;display:flex;gap:6px;flex-wrap:wrap;">
              <span class="badge" style="background:#fffbeb;color:#92400e;padding:2px 8px;border-radius:10px;font-size:13px;font-weight:500;">Monitored event</span>
              ${
                // OMON-179/177: the entry's frozen per-shift policy values.
                m.mandatory === true ? `<span class="badge" style="background:#fef2f2;color:#991b1b;padding:2px 8px;border-radius:10px;font-size:13px;font-weight:500;">🛑 Mandatory</span>` : ""}
              ${m.escalate === true ? `<span class="badge" style="background:#fde68a;color:#92400e;padding:2px 8px;border-radius:10px;font-size:13px;font-weight:500;">&#9888; Escalates${typeof m.escalationThreshold === "number" ? ` ${m.escalationDirection === "below" ? "&lt;" : "&gt;"} ${m.escalationThreshold}` : ""}</span>` : ""}
              ${typeof m.targetValue === "number" ? `<span class="badge" style="background:#eff6ff;color:#2563eb;padding:2px 8px;border-radius:10px;font-size:13px;font-weight:500;">Target &le; ${m.targetValue}/shift</span>` : ""}
              ${m.allowNotApplicable === true ? `<span class="badge" style="background:#f3f4f6;color:#374151;padding:2px 8px;border-radius:10px;font-size:13px;font-weight:500;" title="${escapeHtml(ALLOW_NOT_APPLICABLE_HELP)}">${escapeHtml(ALLOW_NOT_APPLICABLE_CHIP)}</span>` : ""}
              ${m.lifecycleStatus
                ? `<span class="badge badge-${m.lifecycleStatus === "active" ? "active" : "draft"}">${escapeHtml(m.lifecycleStatus)}</span>`
                : ""}
            </div>
            ${monitoredChartHTML(m, state.patientNarrative)}
          </div>
        `).join("");
        tabContentHtml = `
          <p style="font-size:13px;color:#6b7280;margin-bottom:8px;">Per-shift recording obligations &middot; recorded by carers during visits, each per its own regime.</p>
          <div class="chart-zoom-hint" style="margin-bottom:8px;display:flex;align-items:center;gap:8px;flex-wrap:wrap;">
            <span>Showing the latest month &middot; ctrl+scroll or pinch to zoom out for the full history &middot; shift+drag to pan</span>
            <span id="monitoredRange" class="chart-range-badge" hidden></span>
            <button id="monitoredResetZoom" class="chart-range-reset" type="button" hidden>Back to today</button>
          </div>
          ${rows}
        `;
      } else {
        tabContentHtml = `<div class="empty-state" style="padding:24px;">
          <h3>No monitored events on this plan yet</h3>
          <p>Add some via Create new draft &rarr; Add Monitored Events.</p>
        </div>`;
      }
    } else if (dailyLogActive) {
      // OMON-205 f/u: the diary as a first-class read. Rendered over the WHOLE
      // plan rather than the trailing review window the monitored-event
      // swimlanes use — a running record that silently truncates is worse than
      // no record. One section per diary trackable (usually exactly one).
      tabContentHtml = diaryEvents.map((m) => `
        <div class="card goal-card" style="border-left:3px solid #991b1b;">
          <h3>${escapeHtml(m.title)}</h3>
          ${m.prompt ? `<p style="font-size:13px;color:#374151;margin-top:4px;white-space:pre-wrap;">${escapeHtml(m.prompt)}</p>` : ""}
          ${partPromptsHtml(m)}
          <div style="margin-top:6px;display:flex;gap:6px;flex-wrap:wrap;">
            <span class="badge" style="background:#f3f4f6;color:#991b1b;padding:2px 8px;border-radius:10px;font-size:13px;font-weight:500;">${m.recordingKind === "incident" ? "Incident log" : "Shift log"}</span>
            ${m.mandatory === true ? `<span class="badge" style="background:#fef2f2;color:#991b1b;padding:2px 8px;border-radius:10px;font-size:13px;font-weight:500;">🛑 Mandatory</span>` : ""}
          </div>
          ${diaryTimelineAll(m.odCode, state.patientNarrative, m.recordingKind)}
        </div>
      `).join("");
    } else if (narrativeActive) {
      // OMON-129: the single-plan transcript — a read-projection over the
      // same observations the charts use (renderer owns the policy).
      tabContentHtml = renderCombinedNarrative({ foldable: true });
    }
  }

  // Apple-restyle 2026-07-24: the client information tile — identity column
  // (Contacts header kept vertical) + inset-grouped Care / Family lists.
  // Round actions carry the header's verbs: ⬇ print/save a copy (any plan or
  // draft — chooser when both), ☰ jump to Combined narrative (plan), ⌂ this
  // client's Connect timeline.
  const ctx = state.clientContext;
  const ageText = ctx?.age !== undefined ? `Age ${ctx.age}` : "Age unknown";
  const subText = `${ageText}${ctx?.addressSummary ? ` · ${escapeHtml(ctx.addressSummary)}` : ""}`;
  const heroActions = [
    plan || state.patientDraft
      ? `<button class="ct-act" id="downloadPlanBtn" title="Print or save a plan copy">⬇︎</button>` : "",
    // Ed 2026-09-08 (ADR-0040 phase 0): the ☰ shift-log shortcut is
    // repurposed — the Shift log has its own tab; this fold-out shows the
    // week's rota READ-ONLY (real OneTouch shifts in prod, synthetic rotas
    // in demo). Unconditional: rotas exist plan or no plan.
    `<button class="ct-act" id="ctRotaBtn" title="Weekly rota (read-only)">
       <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><rect x="3" y="5" width="18" height="16" rx="2"/><line x1="3" y1="10" x2="21" y2="10"/><line x1="8" y1="3" x2="8" y2="7"/><line x1="16" y1="3" x2="16" y2="7"/></svg>
     </button>`,
    `<button class="ct-act" id="ctConnectBtn" title="Connect sensor view">⌂</button>`,
    // MONO-106 gap (Ed, 2026-09-08): upload existed, removal didn't — a bad
    // avatar was stuck until replaced. Only offered when a photo exists.
    ctx?.clientAvatarPath
      ? `<button class="ct-act" id="ctAvatarDelBtn" title="Remove client photo">✕</button>` : "",
  ].join("");

  clientsView.innerHTML = `
    <div class="print-plan-region">
      <div class="ct-hero-bg">
        <div class="ct-card">
          <div class="ct-identity">
            <span class="ct-avatar-wrap" id="clientAvatarWrap" title="Set client photo">
              ${renderAvatar(p.name, ctx?.clientAvatarPath, 104, "clientAvatar")}
              <span class="ct-edit-chip">edit</span>
            </span>
            <h2 class="ct-who-name">${escapeHtml(p.name)}</h2>
            <div class="ct-who-sub">${subText}</div>
            <div class="ct-actions">${heroActions}</div>
            <div class="ct-eoc">EoC ${escapeHtml(p.docId)}</div>
          </div>
          <div class="ct-groups">
            ${renderCareGroup(ctx, plan?.periodStart)}
            ${renderFamilyContacts(ctx)}
          </div>
        </div>
      </div>
      <div class="ct-scrim" id="ctScrim"></div>
      <div class="rota-fold" id="ctRotaFold" hidden></div>
      ${draftResumeBanner}
      ${planHtml}
      ${tabsHtml}
      ${tabContentHtml}
    </div>
  `;

  // MONO-106: hydrate avatar photos (rules-gated reads) + wire the upload.
  hydrateAvatars(clientsView);
  wireAvatarUpload(clientsView);

  // Contact rows open the vertical Contacts card; scrim/✕/Escape close it.
  const familyContacts = state.clientContext?.familyContacts ?? [];
  clientsView.querySelectorAll<HTMLElement>("[data-contact-idx]").forEach((el) => {
    el.addEventListener("click", () => {
      const c = familyContacts[Number(el.getAttribute("data-contact-idx"))];
      if (c) openContactCard(c);
    });
  });
  const scrim = clientsView.querySelector<HTMLElement>("#ctScrim");
  scrim?.addEventListener("click", (e) => { if (e.target === scrim) closeContactCard(); });
  if (!escCloseWired) {
    escCloseWired = true;
    document.addEventListener("keydown", (e) => { if (e.key === "Escape") closeContactCard(); }, { passive: true });
  }

  // Identity-column round actions (downloadPlanBtn keeps its id + handler).
  // The calendar toggles the read-only weekly rota fold-out (lazy fetch;
  // ADR-0040 phase 0 — see rota-week.ts). The Shift log stays reachable
  // via its tab; the old ☰ shortcut was retired 2026-09-08.
  {
    const rotaBtn = clientsView.querySelector<HTMLElement>("#ctRotaBtn");
    const rotaFold = clientsView.querySelector<HTMLElement>("#ctRotaFold");
    if (rotaBtn && rotaFold) wireRotaFoldout(rotaBtn, rotaFold, p.docId);
  }
  clientsView.querySelector("#ctConnectBtn")?.addEventListener("click", () => {
    go({ kind: "connect-client", eocId: p.docId });
  });

  // MONO-106: remove the client photo via the avatar_delete callable
  // (admin-gated server-side; clears photo[] + paired audit + best-effort
  // derivative cleanup). One click, no confirm dialog: the action is
  // audited and reversible by re-uploading, and browser-modal confirms
  // wedge automation. The tile re-renders back to the monogram.
  clientsView.querySelector("#ctAvatarDelBtn")?.addEventListener("click", async (e) => {
    const btn = e.currentTarget as HTMLButtonElement;
    const patientDocId = state.clientContext?.patientDocId;
    if (!patientDocId) return;
    btn.disabled = true;
    btn.textContent = "…";
    try {
      const { getFunctions, httpsCallable } = await import("firebase/functions");
      const { app } = await import("../firebase");
      const call = httpsCallable(getFunctions(app, "europe-west2"), "avatar_delete");
      await call({ ownerCollection: "_patient", ownerDocId: patientDocId });
      if (state.selectedPatient) {
        const { loadClientContext } = await import("../loaders/client-context");
        state.clientContext = await loadClientContext(state.selectedPatient);
      }
      renderPatientDetail();
    } catch (err) {
      console.warn("avatar delete failed", err);
      btn.disabled = false;
      btn.textContent = "✕";
    }
  });

  // Bind events — return-to-list now happens via clicking the "Client Plans"
  // nav tab (see switchToTab).
  // Header-rework 2026-07-24: ONE plan action, derived from state. The old
  // top-right tri-state button ("Create new draft"/"Resume Draft") is gone —
  // its resume branch set builderMode="create" against an existing plan (the
  // "New Care Plan" header bug) and duplicated the banner's Resume editing.
  // createPlanBtn exists only in the no-plan empty state, so this handler is
  // pure fresh-seed by construction.
  // ☠️ The builder walks the WHOLE catalogue, and a plan's read-only view now
  // loads only the definitions it names (2026-09-17). So every way into the
  // builder awaits ensureCatalogue() first — usually instant, because the
  // plan view prefetches it in the background.
  clientsView.querySelector("#createPlanBtn")?.addEventListener("click", async () => {
    await ensureCatalogue();
    state.builderMode = "create";
    state.builderGoals = [];
    // OMON-205: every new plan starts with the auto-included facts (Daily
    // log) already seeded, so planners never have to remember to add them.
    // Removable per-plan. Also resets any stale ME state from a prior draft.
    state.builderMonitoredEvents = defaultBuilderMonitoredEvents(state.catalogueData);
    state.planMonths = 12;
    state.planStartDate = new Date().toISOString().slice(0, 10);
    state.currentDraftId = null;
    renderClientsView();
  });
  clientsView.querySelector("#editPlanBtn")?.addEventListener("click", async () => {
    await ensureCatalogue();
    editPlan();
  });

  // OMON-329: version history, loaded on demand. Kept out of the main render
  // so opening a client costs no extra reads — a planner only pays for the
  // history when they ask for it.
  clientsView.querySelector("#planHistoryBtn")?.addEventListener("click", async () => {
    const panel = clientsView.querySelector<HTMLElement>("#planHistoryPanel");
    const eocId = state.selectedPatient?.docId;
    if (!panel || !eocId) return;
    if (!panel.hidden) { panel.hidden = true; return; }
    panel.hidden = false;
    panel.innerHTML = `<p style="font-size:13px;color:#6b7280;">Loading version history…</p>`;
    const versions = await loadPlanVersions(eocId);
    if (versions.length === 0) {
      panel.innerHTML = `<p style="font-size:13px;color:#6b7280;">No published versions yet.</p>`;
      return;
    }
    // ☠️ Surface a draft conflict when the panel OPENS, and disable the
    // buttons — do not let someone click into a refusal. Preventing beats
    // refusing: the first cut refused after the click and, because the label
    // was never reset, read as a hung app. A button that cannot work should
    // say so before it is pressed.
    const blocked = Boolean(state.patientDraft);
    panel.innerHTML = `
      <p style="font-size:13px;color:#6b7280;margin-bottom:6px;">
        Restoring publishes a NEW version copied from an older one. Nothing is
        overwritten and no version is deleted.
      </p>
      ${blocked ? `<p style="font-size:13px;color:#92400e;background:#fffbeb;border-radius:6px;padding:8px 12px;margin-bottom:6px;">
        There is a draft in progress for this client (see the banner above), so
        restoring is unavailable. Publish or discard that draft first — this is
        deliberate, so nobody's unpublished work is replaced by a restore.
      </p>` : ""}
      <p id="planHistoryNotice" hidden
         style="font-size:13px;color:#92400e;background:#fffbeb;border-radius:6px;padding:8px 12px;margin-bottom:6px;"></p>
      ${versions.map((v: PlanVersionInfo) => {
        const when = v.created ? String(v.created).slice(0, 16).replace("T", " ") : "—";
        const live = v.isLive
          ? `<span class="badge badge-active" style="margin-left:6px;">live</span>`
          : "";
        const counts = `${v.goalCount} goal${v.goalCount === 1 ? "" : "s"}`
          + ` &middot; ${v.monitoredCount} monitored event${v.monitoredCount === 1 ? "" : "s"}`;
        return `
        <div class="plan-version-row${v.isLive ? " is-live" : ""}">
          <span style="width:56px;flex:0 0 auto;font-weight:650;">v${v.version ?? "?"}</span>
          <span style="width:132px;flex:0 0 auto;color:#6b7280;font-variant-numeric:tabular-nums;">${escapeHtml(when)}</span>
          <span style="flex:1;color:#374151;">${counts}${live}</span>
          ${v.isLive ? "" : `<button class="btn-secondary"
             data-restore-version="${escapeHtml(v.docId)}"
             data-restore-label="v${v.version ?? "?"} — ${escapeHtml(counts.replace("&middot;", "·"))}"
             ${blocked ? "disabled" : ""}>Restore into draft</button>`}
        </div>`;
      }).join("")}`;

    // ☠️ ALWAYS put the label back, and show a refusal where the click
    // happened. The first cut set "Restoring…" and never reset it, so when
    // the draft guard refused — the case it exists for — the button stayed
    // "Restoring…" and the app read as hung, while the toast explaining why
    // had already faded. A guard nobody can see is worse than no guard: it
    // looks like a fault in the tool rather than a decision by it.
    const notice = panel.querySelector<HTMLElement>("#planHistoryNotice");
    panel.querySelectorAll<HTMLButtonElement>("[data-restore-version]").forEach((btn) => {
      btn.addEventListener("click", async () => {
        const docId = btn.getAttribute("data-restore-version");
        if (!docId) return;
        // ☠️ Confirm which version, by name. Not because restoring is
        // dangerous — it only creates a draft — but because on a wide screen
        // the button sits a long way from the version it belongs to, so the
        // real risk is acting on the WRONG ROW. The dialog names the version
        // and its counts, which is the only thing that closes that loop.
        const what = btn.getAttribute("data-restore-label") || "this version";
        if (!(await confirmRestore(what))) return;
        const label = btn.textContent || "Restore into draft";
        btn.textContent = "Restoring…";
        btn.disabled = true;
        if (notice) notice.hidden = true;
        try {
          const result = await restorePlanVersion(docId);
          if (!result.ok && notice) {
            notice.textContent = result.reason || "Could not restore that version.";
            notice.hidden = false;
          }
        } catch (err) {
          console.warn("restore failed", err);
          if (notice) {
            notice.textContent = "Could not restore that version — nothing was changed.";
            notice.hidden = false;
          }
        } finally {
          // finally, not the happy path: a throw must not leave the button
          // stuck either.
          btn.textContent = label;
          btn.disabled = false;
        }
      });
    });
  });
  clientsView.querySelector("#downloadPlanBtn")?.addEventListener("click", () => openPrintChooser());

  // OMON-035: draft-resume banner controls.
  clientsView.querySelector("#resumeDraftBtn")?.addEventListener("click", async () => {
    if (!state.patientDraft || !state.patientDraft._draftState) return;
    await ensureCatalogue(); // restoreDraftState reads definitions (prompts)
    restoreDraftState(state.patientDraft._draftState);
    state.currentDraftId = state.patientDraft.docId;
    state.builderMode = state.patientCarePlans.length > 0 ? "edit" : "create";
    renderClientsView();
  });
  clientsView.querySelector("#discardDraftBtn")?.addEventListener("click", () => {
    confirmAndDiscardDraft();
  });

  // Bind chart category collapse/expand
  clientsView.querySelectorAll<HTMLElement>("[data-chart-cat]").forEach((el) => {
    el.addEventListener("click", () => {
      const cat = el.getAttribute("data-chart-cat")!;
      if (state.chartCollapsedCategories.has(cat)) state.chartCollapsedCategories.delete(cat);
      else state.chartCollapsedCategories.add(cat);
      renderPatientDetail();
    });
  });

  // OMON-121: tab toggle (Developmental goals vs Tasks vs Combined narrative).
  // OMON-202: navigate via the URL so each tab is a back-able, bookmarkable
  // route (#/clients/<eocId>/<tab>). The router re-renders detail in place
  // (no plan reload) since the patient is already selected.
  clientsView.querySelectorAll<HTMLElement>("[data-detail-tab]").forEach((el) => {
    el.addEventListener("click", () => {
      const tab = el.getAttribute("data-detail-tab");
      if ((tab === "developmental" || tab === "tasks" || tab === "dailylog" || tab === "narrative")
          && state.selectedPatient) {
        go({ kind: "client", eocId: state.selectedPatient.docId, tab });
      }
    });
  });

  // Create Chart.js instances after DOM is ready. Both the developmental-goal
  // trajectory charts and the monitored-event review charts (OMON-201) hydrate
  // here and register on state.activeCharts, so the destroy loop at the top of
  // renderPatientDetail tears them all down together on the next render.
  createTrajectoryCharts();
  createMonitoredCharts();
}
